import * as Sentry from '@sentry/react'
import { ModuleRegistry } from 'ag-grid-community'
import {
  AllEnterpriseModule,
  ServerSideRowModelModule,
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  LicenseManager,
} from 'ag-grid-enterprise'
import { createRoot } from 'react-dom/client'
import { Portal } from 'react-portal'
import { BrowserRouter as Router } from 'react-router-dom'

import type { ReactElement } from 'react'

import App from './App'
import { SENTRY_COMMONS } from './const'
import { isProduction } from './utils/env'
import { DSN, ENVIRONMENT, RELEASE } from './utils/sentry'

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-066386}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{MWM_SAS}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{MWM_Intranet}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{MWM_Intranet}_need_to_be_licensed___{MWM_Intranet}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{2_November_2025}____[v3]_[01]_MTc2MjA0MTYwMDAwMA==bd71b33f43781157f7c30f41566b332b'
)

// Register all enterprise features
ModuleRegistry.registerModules([
  AllEnterpriseModule,
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  ServerSideRowModelModule,
])

if (isProduction()) {
  Sentry.init({
    ...SENTRY_COMMONS,
    dsn: DSN,
    environment: ENVIRONMENT,
    integrations: [Sentry.replayIntegration(), Sentry.extraErrorDataIntegration()],
    release: RELEASE,

    // Session Replay
    replaysOnErrorSampleRate: 0.8, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  })
}

const ErrorFallback = (): ReactElement => {
  return (
    <Portal>
      <div className="error-boundary-container">
        <div className="error-boundary-fallback">
          <p className="sorry">We are sorry an error has occurred</p>
          <p className="link">
            Refresh the page or return <a href="/">home</a>
          </p>
        </div>
      </div>
    </Portal>
  )
}

const container = document.getElementById('root')
const root = createRoot(container!) // eslint-disable-line
root.render(
  <Router>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <App />
    </Sentry.ErrorBoundary>
  </Router>
)

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.ready.then((registration) => {
    registration.unregister()
  })
}
